import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from 'store';

export type AreaStatus = {
  areaNo: number;
  foundClue_1: boolean;
  foundClue_2: boolean;
  foundClue_3: boolean;
  completed: boolean;
  receivedClearCard: boolean;
  foundClue?: Record<number, boolean>;
};

export type MissionStatus = {
  missionNo: number;
  completed: boolean;
  receivedClearCard: boolean;
};

export type ProgressResponse = {
  isOnboardingCompleted: boolean;
  hasCompletedOnboarding: boolean;
  areaStatusList: AreaStatus[];
  setAreaStatusList: number[];
  recentlyExploredAreaNo: number | null;
  rewardList: Array<'halfway' | 'almost' | 'perfect'>;
  completedAreaList: number[];
  missionStatusList: MissionStatus[];
};

export type ErrorResponse = {
  message: string;
};

export type ProgressAPIResponse = {
  '200': ProgressResponse;
  '401': ErrorResponse;
  '500': ErrorResponse;
};

const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';

/**
 * オンボーディングの完了をAPIサーバーに通知し、その結果を更新します。
 * 成功した場合はオンボーディングが完了したというステートを更新し、
 * 失敗した場合はエラー情報を返します。
 * @returns {Promise<ProgressResponse>} オンボーディングが完了したときの進行状況
 * @throws {ErrorResponse} オンボーディングの完了通知が失敗した場合のエラー
 * @example
 * // オンボーディング完了をAPIサーバーに通知
 * dispatch(completeOnboarding());
 */
export const completeOnboarding = createAsyncThunk<
  ProgressResponse,
  void,
  { rejectValue: ErrorResponse }
>('progress/completeOnboarding', async (_, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const userToken = state.liffUser.userToken;
  const response = await fetch(`${baseUrl}/complete-onboarding`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${userToken}`,
    },
  });

  if (response.status !== 200) {
    const error: ErrorResponse = await response.json();
    return rejectWithValue(error);
  }

  const data: ProgressResponse = await response.json();
  return data;
});

// progressAPIの作成
export const progressAPI = createApi({
  reducerPath: 'progressAPI',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // ここにカスタムヘッダーを追加します
      // 現在の状態にアクセスします
      const state = getState() as RootState;
      // 状態からユーザートークンを取得します
      const userToken = state.liffUser.userToken;
      headers.set('Content-Type', 'application/json');
      headers.set('Authorization', `Bearer ${userToken}`);
      return headers;
    },
  }),
  tagTypes: ['progress'],
  endpoints: builder => ({
    getProgress: builder.query<ProgressResponse, void>({
      query: () => ({
        url: '/progress',
      }),
    }),
    completeOnboarding: builder.mutation<ProgressResponse, void>({
      query: () => ({
        url: '/complete-onboarding',
        method: 'POST',
      }),
    }),
  }),
});

export const { useGetProgressQuery, useCompleteOnboardingMutation } = progressAPI;

// progressSliceの作成
export const progressSlice = createSlice({
  name: 'progress',
  initialState: {
    hasCompletedOnboarding: false,
    requestedDirectory: '/',
  } as ProgressResponse & { requestedDirectory: string },
  reducers: {
    setRequestedDirectory: (state, action: PayloadAction<string>) => {
      state.requestedDirectory = action.payload;
    },
    setIsOnboardingCompleted: (state, action: PayloadAction<boolean>) => {
      state.isOnboardingCompleted = action.payload;
    },
    setHasCompletedOnboarding: (state, action: PayloadAction<boolean>) => {
      state.hasCompletedOnboarding = action.payload;
    },
    setAreaStatusList: (state, action: PayloadAction<AreaStatus[]>) => {
      state.areaStatusList = action.payload;
    },
    setCompletedAreaList: (state, action: PayloadAction<number[]>) => {
      state.completedAreaList = action.payload;
    },
    setRecentlyExploredAreaNo: (state, action: PayloadAction<number | null>) => {
      state.recentlyExploredAreaNo = action.payload;
    },
    setRewardList: (state, action: PayloadAction<Array<'halfway' | 'almost' | 'perfect'>>) => {
      state.rewardList = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(completeOnboarding.fulfilled, (state, action) => {
      // オンボーディングが完了したら、ステートを更新します
      state.isOnboardingCompleted = action.payload.isOnboardingCompleted;
      state.areaStatusList = action.payload.areaStatusList;
      state.completedAreaList = action.payload.completedAreaList;
      state.recentlyExploredAreaNo = action.payload.recentlyExploredAreaNo;
      state.rewardList = action.payload.rewardList;

      progressAPI.util.invalidateTags(['progress']);
    });
  },
});

export const {
  setIsOnboardingCompleted,
  setHasCompletedOnboarding,
  setAreaStatusList,
  setCompletedAreaList,
  setRecentlyExploredAreaNo,
  setRewardList,
  setRequestedDirectory,
} = progressSlice.actions;

export default progressSlice.reducer;
